import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '../services/lib/toast';
import { Music, ChevronLeft, ChevronRight, Mail, CheckCircle2, XCircle, Clock, ExternalLink, Check, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/shared';
import { spaNavigate } from '../../lib/spaNavigate';
import { useServicesMode } from '../services/hooks/useServicesMode';
import { svcGet, svcPost, errorText } from '../services/lib/api';
import { fmtDate, fmtTime, formatName, toQuickComposePlan, passedBy } from '../services/lib/format';
import StatusPill from '../services/components/StatusPill';

const PC_SCHEDULE_URL = 'https://services.planningcenteronline.com/schedule';
const CARD = 'card-lift shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card hover:shadow-xl hover:shadow-slate-200/60 min-h-[280px] flex flex-col group';
const NAV_BTN = 'h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-all';
const MAX_ROSTER = 4;

// StatusIcon pattern from PlanningCenterWidget.js (accepted = confirmed).
function StatusIcon({ status }) {
  if (status === 'accepted') return <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500 flex-shrink-0" />;
  if (status === 'declined') return <XCircle className="w-[18px] h-[18px] text-red-400 flex-shrink-0" />;
  return <Clock className="w-[18px] h-[18px] text-amber-400 flex-shrink-0" />;
}

function Header({ children }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5 min-w-0">
        <div data-tile="services" className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md shadow-teal-500/25">
          <Music className="w-4 h-4 text-white" />
        </div>
        {/* C10: one line, like the Planning Center widget at the same card width */}
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">Upcoming Services</h3>
      </div>
      {children && <div className="flex items-center gap-0.5 flex-shrink-0">{children}</div>}
    </div>
  );
}

function SongsLine({ songs }) {
  if (!songs?.length) return null;
  // C11: the ellipsized line carries the full list as a title.
  const line = songs.map((s) => (s.key ? `${s.title} (${s.key})` : s.title)).join(' · ');
  return (
    <div className="border-t border-slate-100 dark:border-white/10 pt-3 mt-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Music className="w-3.5 h-3.5 text-teal-400" />
        <span className="text-sm text-slate-400 dark:text-slate-500">{songs.length} {songs.length === 1 ? 'song' : 'songs'}</span>
      </div>
      <p className="text-sm text-slate-700 dark:text-slate-200 truncate" title={line} data-testid="services-widget-songs-line">
        {line}
      </p>
    </div>
  );
}

// Dashboard slot 6 for flag-on churches without a Planning Center connection
// on this account (Section 7.5): chooser, admin next services, member next
// assignment. Never rendered when the flag is off (rule 15).
export default function ServicesWidget({ user, mode, onOpenApp, onConnectPlanningCenter, onQuickMessage }) {
  const userId = user?.id;
  const { isAdmin, hasTemplate, resolved } = useServicesMode(userId);
  const [next, setNext] = useState(null);
  const [index, setIndex] = useState(0);
  const [details, setDetails] = useState({});
  const [responding, setResponding] = useState(false);

  const loadNext = useCallback(async () => {
    if (!userId || mode !== 'simple') return;
    try {
      setNext(await svcGet('/next', userId));
    } catch {
      setNext({ mode, error: true });
    }
  }, [userId, mode]);

  useEffect(() => { loadNext(); }, [loadNext]);

  // Fix 9 B5: once a service has started it can't be answered (A-04), as /mine shows
  // (H27). /next carries no church zone, so on the service day (only then) the member
  // card asks /mine/{id}, whose can_change uses the same start instant as respond.
  const myNext = !isAdmin ? next?.my_next : null;
  const mySid = myNext?.status === 'pending' ? myNext.service?.id : null;
  const myNearToday = !!mySid && (myNext.service.date || '') <= new Date(Date.now() + 36 * 3600 * 1000).toISOString().slice(0, 10);
  const [startCheck, setStartCheck] = useState(null);   // {sid, canChange}
  useEffect(() => {
    if (!mySid || !myNearToday) return undefined;
    let cancelled = false;
    svcGet(`/mine/${mySid}`, userId)
      .then((d) => { if (!cancelled) setStartCheck({ sid: mySid, canChange: (d?.my_assignments || []).some((a) => a.can_change) }); })
      .catch(() => { if (!cancelled) setStartCheck({ sid: mySid, canChange: true }); })   // unknown: as before
    return () => { cancelled = true; };
  }, [mySid, myNearToday, userId, next]);
  const myCanAnswer = !myNearToday || (startCheck?.sid === mySid && startCheck.canChange);

  const services = next?.role === 'admin' ? next.services || [] : [];
  const current = services[Math.min(index, Math.max(services.length - 1, 0))];

  // The roster rows for the card being shown (GET /{id}, admin only).
  useEffect(() => {
    if (!current || details[current.id] || !isAdmin) return;
    let cancelled = false;
    svcGet(`/${current.id}`, userId)
      .then((d) => { if (!cancelled) setDetails((prev) => ({ ...prev, [current.id]: d })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [current, details, isAdmin, userId]);

  const openPlan = (serviceId) => onOpenApp?.('services', { tab: 'plans', serviceId: serviceId || null });

  const quickMessage = async (serviceId) => {
    try {
      const roster = await svcPost('/quick-message-roster', userId, { service_id: serviceId });
      onQuickMessage?.(toQuickComposePlan(roster));
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const respond = async (status) => {
    const rows = next?.my_next?.positions || [];
    if (!rows.length || responding) return;
    setResponding(true);
    try {
      // One call per assignment (ruling Q1).
      for (const r of rows) {
        await svcPost(`/assignments/${r.assignment_id}/respond`, userId, { status });
      }
      toast.success(status === 'accepted' ? "You're in" : 'Your leader has been told');
      await loadNext();
    } catch (err) {
      toast.error(errorText(err));
      await loadNext();
    } finally {
      setResponding(false);
    }
  };

  // B2 (R-14): the admin's own rows on the shown service, answered through
  // the same respond endpoint, one row per call (ruling Q1).
  const respondAdmin = async (rows, status) => {
    if (!rows.length || responding || !current) return;
    setResponding(true);
    try {
      for (const r of rows) {
        await svcPost(`/assignments/${r.id}/respond`, userId, { status });
      }
      toast.success(status === 'accepted' ? "You're in" : 'Declined');
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setResponding(false);
      setDetails((prev) => { const next = { ...prev }; delete next[current.id]; return next; });
      await loadNext();
    }
  };

  // ── Planning Center church (this account has no Planning Center token) ──
  if (mode === 'pco') {
    return (
      <Card className={CARD} data-testid="services-widget-pco">
        <CardContent className="p-5 flex flex-col flex-1">
          <Header />
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 py-6">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Your church uses Planning Center</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Ask your leader to connect you in Planning Center settings.</p>
            <a href={PC_SCHEDULE_URL} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 dark:text-teal-300 underline underline-offset-2" data-testid="services-widget-pco-schedule">
              Open my Planning Center schedule <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!resolved || next === null) {
    return (
      <Card className="shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card animate-pulse" data-testid="services-widget-loading">
        <CardContent className="p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-[#3e4145]" />
            <div className="h-5 w-28 bg-slate-200 dark:bg-[#3e4145] rounded" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-full bg-slate-100 dark:bg-[#3a3d41] rounded" />
            <div className="h-4 w-3/4 bg-slate-100 dark:bg-[#3a3d41] rounded" />
            <div className="h-4 w-1/2 bg-slate-100 dark:bg-[#3a3d41] rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Chooser (admin, nothing set up yet) ──
  if (isAdmin && !hasTemplate) {
    return (
      <Card className={CARD} data-testid="services-chooser">
        <CardContent className="p-5 flex flex-col flex-1">
          <Header />
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 mb-4">Plan your services and ask your team to serve.</p>
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-[#34373c] rounded-lg px-3 py-2.5">
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => onConnectPlanningCenter?.()} data-testid="services-chooser-connect">
                Connect Planning Center
              </Button>
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">Recommended</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-[#34373c] rounded-lg px-3 py-2.5">
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => openPlan(null)} data-testid="services-chooser-setup">
                Use the Simple Planner
              </Button>
            </div>
          </div>
          <p className="mt-4 pt-3 border-t border-slate-100 dark:border-white/10 text-xs text-slate-500 dark:text-slate-400">
            Need file uploads, check-ins, or multi-campus? Use Planning Center.
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Member ──
  if (!isAdmin) {
    const my = next?.my_next;
    if (!hasTemplate) {
      return (
        <Card className={CARD} data-testid="services-widget">
          <CardContent className="p-5 flex flex-col flex-1">
            <Header />
            <div className="flex-1 flex items-center justify-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Your leader hasn't set up services yet.</p>
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className={CARD} data-testid="services-widget">
        <CardContent className="p-5 flex flex-col flex-1">
          <Header />
          {my ? (
            <div className="mt-3 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {fmtDate(my.service.date, 'EEE, MMM d')} · {fmtTime(my.service.service_time)}
                  {my.service.soundcheck_time ? ` · soundcheck ${fmtTime(my.service.soundcheck_time)}` : ''}
                </p>
                <StatusPill status={my.status} />
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(my.positions || []).map((p) => (
                  <span key={p.assignment_id} className="inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">{p.position}</span>
                ))}
              </div>
              {my.status === 'pending' && myCanAnswer && (
                <div className="flex gap-2">
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => respond('accepted')} disabled={responding} data-testid="services-widget-accept">
                    <Check className="w-4 h-4 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400" onClick={() => respond('declined')} disabled={responding} data-testid="services-widget-decline">
                    <X className="w-4 h-4 mr-1" /> Decline
                  </Button>
                </div>
              )}
              <SongsLine songs={my.songs} />
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-1.5 py-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">You're not scheduled yet</p>
              {next?.next_church_service && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Next service {fmtDate(next.next_church_service.date, 'EEE, MMM d')} · {fmtTime(next.next_church_service.service_time)}
                </p>
              )}
              <button type="button" onClick={() => spaNavigate('/team?tab=services')} className="mt-1 text-sm font-medium text-teal-700 dark:text-teal-300 underline underline-offset-2" data-testid="services-widget-blockouts">
                Block out dates
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Admin: next services carousel ──
  const total = services.length;
  const i = Math.min(index, Math.max(total - 1, 0));
  const d = current ? details[current.id] : null;
  const roster = (d?.assignments || []).filter((a) => a.user_id && ['pending', 'accepted', 'declined'].includes(a.status));
  const counts = current?.counts || {};
  const people = (counts.accepted || 0) + (counts.pending || 0) + (counts.declined || 0);
  const myRows = (d?.assignments || []).filter((a) => a.user_id === userId && ['pending', 'accepted', 'declined'].includes(a.status));
  const myPending = myRows.filter((a) => a.status === 'pending');
  const canAnswer = !!d && d.status === 'published' && myRows.length > 0 && !passedBy(d, 0);

  return (
    <Card className={CARD} data-testid="services-widget">
      <CardContent className="p-5 flex flex-col flex-1">
        {/* C10: Send service details (U33, was Quick message) sits with Open plan below, so the title and the
            pager keep one line each on a ~350px card (and at 390px). */}
        <Header>
          {total > 1 && (
            <>
              <button type="button" onClick={() => setIndex((x) => Math.max(0, x - 1))} disabled={i === 0} className={`${NAV_BTN} disabled:opacity-40`} aria-label="Previous service" data-testid="services-widget-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums whitespace-nowrap" data-testid="services-widget-pager">{i + 1} / {total}</span>
              <button type="button" onClick={() => setIndex((x) => Math.min(total - 1, x + 1))} disabled={i >= total - 1} className={`${NAV_BTN} disabled:opacity-40`} aria-label="Next service" data-testid="services-widget-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </Header>

        {!current ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <Music className="w-6 h-6 text-slate-300" />
            <p className="text-sm text-slate-400 dark:text-slate-500">No upcoming services scheduled</p>
            <Button size="sm" variant="outline" onClick={() => openPlan(null)} data-testid="services-widget-open">Open plan</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center flex-wrap gap-2 mt-3 mb-1">
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                {fmtDate(current.date, 'EEE, MMM d')}{current.service_time ? ` · ${fmtTime(current.service_time)}` : ''}
              </p>
              <StatusPill status={current.status} />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3" data-testid="services-widget-counts">
              {counts.accepted || 0} of {people} confirmed · {counts.pending || 0} pending
            </p>
            {roster.length > 0 && (
              <div className="grid grid-cols-2 gap-1.5">
                {roster.slice(0, MAX_ROSTER).map((a) => (
                  <div key={a.id} className="flex items-center gap-2 bg-slate-50/80 dark:bg-white/[0.03] rounded-lg px-2.5 py-1.5 min-w-0">
                    <UserAvatar userId={a.user_id} name={a.user_name} size="xs" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-slate-700 dark:text-slate-200 truncate" title={a.user_name}>{formatName(a.user_name)}</span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400 truncate" title={a.position}>{a.position}</span>
                    </span>
                    <StatusIcon status={a.status} />
                  </div>
                ))}
              </div>
            )}
            {canAnswer && (
              <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="services-widget-my-answer">
                <span className="text-sm text-slate-700 dark:text-slate-200 min-w-0 truncate">You're on {[...new Set(myRows.map((a) => a.position))].join(' and ')}</span>
                <StatusPill status={myPending.length ? 'pending' : (myRows.every((a) => a.status === 'declined') ? 'declined' : 'accepted')} />
                <div className="flex items-center gap-2 w-full">
                  {myPending.length > 0 ? (
                    <>
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => respondAdmin(myPending, 'accepted')} disabled={responding} data-testid="services-widget-admin-accept">
                        <Check className="w-4 h-4 mr-1" /> Accept
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400" onClick={() => respondAdmin(myRows.filter((a) => a.status !== 'declined'), 'declined')} disabled={responding} data-testid="services-widget-admin-decline">
                        <X className="w-4 h-4 mr-1" /> Decline
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => respondAdmin(myRows, myRows.some((a) => a.status === 'accepted') ? 'declined' : 'accepted')} disabled={responding} data-testid="services-widget-admin-change">
                      Change my answer
                    </Button>
                  )}
                </div>
              </div>
            )}
            <SongsLine songs={current.songs} />
            <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => openPlan(current.id)} data-testid="services-widget-open">
                Open plan
              </Button>
              <Button size="sm" variant="outline" onClick={() => quickMessage(current.id)} data-testid="services-widget-quick-message">
                <Mail className="w-4 h-4 mr-1.5" /> Send service details
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
